import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@/lib/router-compat';
import { ShoppingBag } from 'lucide-react';
import { CategoryDealsPage, dealsQuery, type Search } from './DealsPages';
import { comparisonQuery } from '@/components/comparison/ComparisonPages';
import { money } from '@/lib/comparison';
import { seoHead } from '@/lib/seoHead';
import { AUDIENCES, audAdj, type DealsResult } from '@/lib/deals.functions';
import type { QueryClient } from '@tanstack/react-query';

export async function loadCategory(qc: QueryClient, deps: Search & { categorySlug: string; subSlug?: string; audience?: string }) {
  const d = await qc.ensureQueryData(dealsQuery(deps));
  if (d.categoryInfo) await qc.ensureQueryData(comparisonQuery({ categoryIds: d.categoryInfo.ids, limit: 8 }));
  return d;
}

export function categoryHead(d: DealsResult | undefined, path: string, page?: number, filtered = false): ReturnType<typeof seoHead> {
  if (filtered && d?.categoryInfo) return { ...categoryHead(d, path, page), meta: [...(categoryHead(d, path, page).meta ?? []).filter((m: any) => m.name !== 'robots'), { name: 'robots', content: 'noindex,follow' }] };
  const col = d?.collection;
  const month0 = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo' }).format(new Date());
  if (col) {
    const pg = page ? ` - Página ${page + 1}` : '';
    return seoHead({
      title: `${col.title} em Promoção ${month0}${pg} | Shopping do Bem`.slice(0, 75),
      description: col.description ?? `${d!.total} ofertas de ${col.title.toLowerCase()} com desconto real nas lojas parceiras. Economize e gere doação para causas sociais sem custo extra.`,
      path: col.canonical_slug ? `/ofertas/${col.canonical_slug}` : page ? `${path}?page=${page}` : path,
      noindex: !col.indexable || !d!.items.length,
    });
  }
  const c = d?.categoryInfo;
  if (!c) return seoHead({ title: 'Categoria não encontrada | Shopping do Bem', description: 'Esta categoria não existe. Veja as ofertas do dia nas lojas parceiras.', noindex: true });
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'America/Sao_Paulo' }).format(new Date());
  const aud = d!.audience ? AUDIENCES[d!.audience] : null;
  const where = aud ? ` ${audAdj(c.name, d!.audience!)}` : c.parent ? ` em ${c.parent.name}` : '';
  const pg = page ? ` - Página ${page + 1}` : '';
  return seoHead({
    title: (c.seo_title ?? `Ofertas de ${c.name}${where} ${month}${pg} | Shopping do Bem`).slice(0, 75),
    description: c.seo_description ?? `${d!.total} ofertas de ${c.name} com desconto real nas lojas parceiras. Compre com economia e gere doação para causas sociais sem custo extra.`,
    path: page ? `${path}?page=${page}` : path,
    noindex: aud ? d!.total < 6 : !d!.items.length,
  });
}

function CompareStrip({ ids }: { ids: string[] }) {
  const { data } = useSuspenseQuery(comparisonQuery({ categoryIds: ids, limit: 8 }));
  if (!data.items.length) return null;
  return <section className="container pt-8"><h2 className="mb-4 text-xl font-bold">Compare preços nesta categoria</h2>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{data.items.map(p => <Link key={p.id} to={`/produto/${p.slug}`} className="flex gap-3 rounded-md border bg-card p-3 hover:border-primary">
      {p.image_url ? <img src={p.image_url} alt="" loading="lazy" className="h-16 w-16 shrink-0 object-contain" /> : <ShoppingBag className="h-10 w-10 text-muted-foreground" />}
      <div className="min-w-0"><p className="line-clamp-2 text-sm font-semibold">{p.title}</p><p className="mt-1 text-xs text-muted-foreground">a partir de {money(Math.min(...p.offers.map(o => o.price)))} · {p.offers.length} lojas</p></div>
    </Link>)}</div></section>;
}

export function CategoryView({ categorySlug, subSlug, audience, search }: { categorySlug: string; subSlug?: string; audience?: string; search: Search }) {
  const { data } = useSuspenseQuery(dealsQuery({ ...search, categorySlug, subSlug, audience }));
  return <CategoryDealsPage categorySlug={categorySlug} subSlug={subSlug} audience={audience} search={search} compare={data.categoryInfo && !data.audience ? <CompareStrip ids={data.categoryInfo.ids} /> : null} />;
}
